<template>
    <div data-test-view="notifications">
        <l-headline
            :title="$t('adminSettings.notificationsHeadlineTitle')"
            :sub-title="$t('adminSettings.notificationsHeadlineSubtitle')"
        ></l-headline>

        <div
            class="notification-filters form-group label-floating"
            data-test-id="notification-filters"
        >
            <div class="row">
                <div class="col-md-6">
                    <div class="datetime-input-wrapper">
                        <div>
                            <l-date-picker
                                id="start-date-selector"
                                :label="$t('adminSettings.notificationsLabelFilterStartDate')"
                                :value="form.startDate"
                                :on-date-change="onUpdateStartDate"
                                data-test-id="start-date-selector"
                            />
                        </div>
                        <div>
                            <l-time-picker
                                :value="form.startDate"
                                :use-meridian-time="teamUsesMeridianTime"
                                data-test-id="start-time-selector"
                                class="ml-1"
                                @change="onUpdateStartTime"
                            />
                        </div>
                    </div>
                </div>

                <div class="col-md-6">
                    <div class="datetime-input-wrapper">
                        <div>
                            <l-date-picker
                                id="end-date-selector"
                                :label="$t('adminSettings.notificationsLabelFilterEndDate')"
                                :value="form.endDate"
                                :on-date-change="onUpdateEndDate"
                                data-test-id="end-date-selector"
                            />
                        </div>
                        <div>
                            <l-time-picker
                                :value="form.endDate"
                                :use-meridian-time="teamUsesMeridianTime"
                                data-test-id="end-time-selector"
                                class="ml-1"
                                @change="onUpdateEndTime"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-1">
                <div class="col-md-6">
                    <l-input-typeahead
                        id="receiver-selector"
                        key="receiver-selector"
                        :label="$t('adminSettings.notificationsLabelFilterReceiver')"
                        :data="selectableUserList"
                        :on-select="onTypeaheadUserSelect"
                        :on-input-change="onTypeaheadUserInputChange"
                        data-test-id="element-select-receiver-typeahead"
                        :placeholder="$t('adminSettings.notificationsReceiverInputPlaceholder')"
                    />

                    <span
                        v-for="(notificationReceiver, index) in form.selectedNotificationReceivers"
                        :key="notificationReceiver._id"
                        class="badge badge-light small clickable pl-1 pr-1 mr-1 mb-1"
                        tabindex="0"
                        @click="onRemoveSelectedUser(index)"
                        @keyup.13="onRemoveSelectedUser(index)"
                    >
                        {{ notificationReceiver.firstname }} {{ notificationReceiver.lastname }}
                        <i class="fa fa-remove ml-1"></i>
                    </span>
                </div>

                <div class="col-md-6">
                    <l-input-typeahead
                        id="category-selector"
                        key="category-selector"
                        :label="$t('adminSettings.notificationsLabelType')"
                        :data="selectableNotificationTypesList"
                        :on-select="onTypeaheadNotificationTypeSelect"
                        :on-input-change="onTypeaheadNotificationTypeInputChange"
                        :serializer="element => element.title"
                        template-type="NotificationType"
                        data-test-id="element-select-type-typeahead"
                        :max-matches="100"
                        :placeholder="$t('adminSettings.notificationsTypeInputPlaceholder')"
                    />

                    <span
                        v-for="(notificationType, index) in form.selectedNotificationTypes"
                        :key="notificationType.type"
                        class="badge badge-light small clickable pl-1 pr-1 mr-1 mb-1"
                        tabindex="0"
                        @click="onRemoveSelectedNotificationType(index)"
                        @keyup.13="onRemoveSelectedNotificationType(index)"
                    >
                        {{ notificationType.title }} <i class="fa fa-remove ml-1"></i>
                    </span>
                </div>
            </div>
        </div>

        <template v-if="notifications.length">
            <ul class="notifications-list mt-2 mb-2" data-test-id="notifications-list">
                <li
                    v-for="notification in notifications"
                    :key="notification._id"
                    v-b-popover.hover.top="
                        notification.isDismissed
                            ? $t('adminSettings.notificationsDismissedInfo')
                            : ''
                    "
                    class="notification"
                    :class="{ 'notification--dismissed': notification.isDismissed }"
                >
                    <div class="notification__type">
                        <div class="notification__title">{{ notification.title }}</div>
                        <div class="notification__date" :title="notification.createdAt">
                            {{ notification.createdAt | moment("MMM DD, YYYY - HH:mm") }}
                        </div>
                        <div class="notification__sender">
                            {{ $t("adminSettings.notificationsSentFrom") }}:
                            <template v-if="isSystemUser(notification.sender._id)">
                                {{ notification.sender.firstname }}
                                {{ notification.sender.lastname }}
                            </template>
                            <template v-else>
                                <a :href="`#/profile/${notification.sender._id}`" target="_blank">
                                    {{ notification.sender.firstname }}
                                    {{ notification.sender.lastname }}
                                </a>
                            </template>
                        </div>
                        <div class="notification__distribution">
                            <div :class="{ 'events-disabled': notification.isDismissed }">
                                <i v-if="notification.isDismissed" class="fa fa-bell-slash" />
                                <template v-else-if="notification.isInternal">
                                    <i
                                        v-b-popover.hover.top="
                                            notification.isRead
                                                ? $t('adminSettings.notificationsStatusRead')
                                                : $t('adminSettings.notificationsStatusUndread')
                                        "
                                        class="fa"
                                        :class="{
                                            'fa-eye': notification.isRead,
                                            'fa-eye-slash': !notification.isRead,
                                        }"
                                    />
                                </template>
                                <i
                                    v-else-if="notification.isExternal"
                                    v-b-popover.hover.top="
                                        $t('adminSettings.notificationsNoInsights')
                                    "
                                    class="fa fa-low-vision"
                                ></i>
                                <i
                                    v-else
                                    v-b-popover.hover.top="
                                        $t('adminSettings.notificationsStatusUnknown')
                                    "
                                    class="fa fa-question-circle"
                                ></i>
                            </div>
                            <div v-if="notification.isInternal">
                                <i
                                    v-b-popover.hover.top="
                                        $t('adminSettings.notificationsVisibleOnDashboard')
                                    "
                                    class="fa fa-user"
                                />
                            </div>
                            <div v-if="notification.isExternal">
                                <i
                                    v-b-popover.hover.top="
                                        $t('adminSettings.notificationsSentViaThirdParty')
                                    "
                                    class="fa fa-envelope"
                                />
                            </div>
                        </div>
                    </div>

                    <i class="notification__divider fa fa-angle-double-right"></i>

                    <component
                        :is="isSystemUser(notification.receiver._id) ? 'div' : 'a'"
                        class="notification__receiver"
                        :href="
                            !isSystemUser(notification.receiver._id)
                                ? `#/profile/${notification.receiver._id}`
                                : ''
                        "
                        target="_blank"
                    >
                        <l-profile-picture
                            v-if="!isSystemUser(notification.receiver._id)"
                            class="mr-2"
                            size="s"
                            :user="notification.receiver"
                        />
                        <div class="truncate">
                            <span
                                >{{ notification.receiver.firstname }}
                                {{ notification.receiver.lastname }}</span
                            >
                        </div>
                    </component>

                    <div class="notification__actions">
                        <div
                            v-b-popover.hover.top="
                                notification.isSent
                                    ? $t('adminSettings.notificationsRescheduleActionHint')
                                    : notification.isExternal
                                    ? $t('adminSettings.notificationsMessageInDelivery')
                                    : $t('adminSettings.notificationsMessageRescheduleNotPossible')
                            "
                            class="inline-block"
                        >
                            <button
                                class="btn btn-secondary btn-raised"
                                :disabled="
                                    !notification.isExternal ||
                                        !notification.isSent ||
                                        notification.isDismissed ||
                                        isLoading.resetNotificationDelivery
                                "
                                type="button"
                                data-test-class="button-re-schedule-delivery"
                                @click="resetNotificationDeliveryStatus(notification._id)"
                            >
                                {{ $t("adminSettings.notificationsRescheduleAction") }}
                            </button>
                        </div>
                    </div>
                </li>
            </ul>

            <button
                v-if="isLoadMoreNotificationsActive"
                class="btn btn-secondary btn-block"
                :disabled="isLoading.getCompanyNotifications"
                type="button"
                data-test-id="button-load-more"
                @click="onLoadMore"
            >
                {{ $t("adminSettings.notificationsLoadMore") }}
            </button>
        </template>
        <l-no-data v-else :text="$t('adminSettings.notificationsNoneFound')" />
    </div>
</template>

<style scoped>
.-mt-4px {
    margin-top: -4px;
}

.inline-block {
    display: inline-block;
}

.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.events-disabled {
    pointer-events: none;
}

.datetime-input-wrapper {
    display: flex;
}

.notification-filters {
    padding: 8px 0;
    background-color: #ffffff;
    border-bottom: 1px solid #e9e9e9;
    z-index: 10;
}

.notifications-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.notification {
    padding: 1rem 2rem;
    align-items: center;
    background-color: var(--color-grey-light);
    border-radius: 5px;
    margin: 4px 0;
}

@media only screen and (min-width: 768px) {
    .notification {
        display: flex;
    }
}

.notification:nth-child(2n) {
    background-color: white;
}

.notification--dismissed {
    opacity: 0.5;
    cursor: not-allowed;
    border-left: 4px solid var(--color-red);
}

.notification__title {
    font-weight: 700;
}

.notification__date {
    color: rgba(85, 85, 85, 0.25);
    font-size: 1rem;
    line-height: 1;
}

.notification__distribution {
    display: flex;
}

.notification__distribution > div {
    margin-right: 1rem;
    font-size: 12px;
    background-color: rgba(255, 255, 255, 0.5);
    cursor: help;
    box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
        rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
    width: 24px;
    height: 24px;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.notification__date,
.notification__sender {
    font-size: 1.25rem;
}

.notification__divider {
    color: rgba(85, 85, 85, 0.25);
    margin: 0 4rem;
    font-size: 2rem;
}

.notification__receiver {
    display: flex;
    align-items: center;
    color: var(--color-grey);
}

.notification .fa {
    color: rgba(85, 85, 85, 0.25);
}

.notification__type,
.notification__receiver {
    max-width: 100%;
}

.notification__type {
    width: 300px;
}

.notification__receiver {
    width: 250px;
}

.notification__type,
.notification__receiver,
.notification__actions {
    flex: 1;
}

.notification__actions {
    text-align: right;
}
</style>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LInputTypeahead from "@/components/LInputTypeahead";
import LHeadline from "@/components/LHeadline";
import LProfilePicture from "@/components/LProfilePicture";
import LDatePicker from "@/components/LDatePicker";
import LTimePicker from "@/components/LTimePicker";
import LNoData from "@/components/LNoData";

export default {
    components: {
        LInputTypeahead,
        LHeadline,
        LProfilePicture,
        LDatePicker,
        LTimePicker,
        LNoData,
    },
    data() {
        LDatePicker;
        return {
            form: {
                startDate: new Date(),
                endDate: new Date(),
                selectedNotificationTypesSearchQuery: "",
                selectedNotificationTypes: [],
                selectedNotificationReceivers: [],
            },
            filter: {
                types: null,
                receivers: null,
                startDate: null,
                endDate: null,
            },
            cursor: null,
            isLoading: {
                resetNotificationDelivery: false,
                getCompanyNotifications: false,
            },
        };
    },
    computed: {
        ...mapGetters({
            notifications: "notifications/adminDashboard/companyNotifications",
            notificationTypes: "notifications/adminDashboard/notificationTypes",
            isLoadMoreNotificationsActive:
                "notifications/adminDashboard/isLoadMoreNotificationsActive",
            usersSearchList: "users/search/list",
            user: "users/current/user",
            teamSettings: "users/current/teamSettings",
        }),
        teamUsesMeridianTime() {
            return this.teamSettings.useMeridianTime || false;
        },
        selectableUserList() {
            return this.usersSearchList.map(format.users);
        },
        selectableNotificationTypesList() {
            let searchQuery = this.form.selectedNotificationTypesSearchQuery || "";
            searchQuery = searchQuery.toLowerCase();

            return this.notificationTypes.filter(notificationType => {
                if (!notificationType) return false;
                if (!searchQuery) return true;
                let title = notificationType.title || "";
                title = title.toLowerCase();

                return title.includes(searchQuery);
            });
        },
        // create url search query
        notificationFilters() {
            const params = [];

            for (const [key, value] of Object.entries(this.filter)) {
                if (value) params.push(`${key}=${encodeURIComponent(value)}`);
            }

            if (this.cursor) params.push(`cursor=${encodeURIComponent(this.cursor)}`);

            return params.length ? `?${params.join("&")}` : "";
        },
    },
    watch: {
        filter: {
            deep: true,
            handler() {
                this.cursor = null;
                this.loadNotifications();
            },
        },
        cursor(newValue) {
            if (!newValue) return;
            this.loadNotifications({ extend: true });
        },
        ["form.selectedNotificationTypes"](newValue) {
            if (!Array.isArray(newValue)) return;
            const types = newValue.map(item => item.type);
            this.filter.types = types.join(",");
        },
        ["form.selectedNotificationReceivers"](newValue) {
            if (!Array.isArray(newValue)) return;
            const receivers = newValue.map(receiver => receiver._id);
            this.filter.receivers = receivers.join(",");
        },
    },
    created() {
        this.form.startDate.setDate(1);
        this.form.startDate.setHours(0, 0, 0);
    },
    methods: {
        isSystemUser(id = 0) {
            return [0, 1].includes(id);
        },
        async resetNotificationDeliveryStatus(notificationId) {
            this.isLoading.resetNotificationDelivery = true;

            this.$store
                .dispatch({
                    type: "notifications/adminDashboard/resetNotificationDelivery",
                    payload: {
                        notificationId,
                    },
                })
                .finally(() => {
                    this.isLoading.resetNotificationDelivery = false;
                });
        },
        async loadNotifications({ extend = false } = {}) {
            this.isLoading.getCompanyNotifications = true;

            this.$store
                .dispatch({
                    type: "notifications/adminDashboard/getCompanyNotifications",
                    payload: {
                        extend,
                        queryParams: this.notificationFilters,
                    },
                })
                .finally(() => {
                    this.isLoading.getCompanyNotifications = false;
                });
        },
        onLoadMore() {
            const lastNotification = this.notifications[this.notifications.length - 1] || {};
            this.cursor = lastNotification._id;
        },
        updateDate(date, type) {
            if (!type || !date) return;

            if (type === "endDate") {
                date.setHours(23, 59, 59);

                if (date < this.form.startDate) {
                    this.form.startDate = date;
                    this.filter.startDate = this.getLocalISODate(date);
                }
            }

            if (type === "startDate") {
                date.setHours(0, 0, 0);

                if (date > this.form.endDate) {
                    this.form.endDate = date;
                    this.filter.endDate = this.getLocalISODate(date);
                }
            }

            this.filter[type] = this.getLocalISODate(date);
            this.form[type] = date;
        },
        onUpdateStartDate(date) {
            this.updateDate(date, "startDate");
        },
        onUpdateEndDate(date) {
            this.updateDate(date, "endDate");
        },
        getLocalISODate(date) {
            return date.toISOString();
        },

        onUpdateStartTime(timestamp) {
            const date = new Date(timestamp);
            if (!this.isValidDateObject(date)) return;

            this.form.startDate.setHours(date.getHours(), date.getMinutes());
            this.filter.startDate = this.getLocalISODate(this.form.startDate);
        },
        onUpdateEndTime(timestamp) {
            const date = new Date(timestamp);
            if (!this.isValidDateObject(date)) return;

            this.form.endDate.setHours(date.getHours(), date.getMinutes());
            this.filter.endDate = this.getLocalISODate(this.form.endDate);
        },
        isValidDateObject(date) {
            return date instanceof Date && !isNaN(date);
        },
        onTypeaheadUserSelect(user = {}) {
            this.form.selectedNotificationReceivers.push(user);
            this.$store.dispatch({ type: "users/search/reset" });
        },
        onTypeaheadUserInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch({ type: "users/search/findByName", query });
            } else {
                this.$store.dispatch({ type: "users/search/reset" });
            }
        },
        onRemoveSelectedUser(index) {
            this.$delete(this.form.selectedNotificationReceivers, index);
        },
        onTypeaheadNotificationTypeSelect(notificationType = {}) {
            // Remove duplicates
            this.form.selectedNotificationTypes.push(notificationType);
            this.form.selectedNotificationTypes = [...new Set(this.form.selectedNotificationTypes)];
            this.form.selectedNotificationTypesSearchQuery = "";
        },
        onTypeaheadNotificationTypeInputChange(query) {
            if (query.length >= 2) {
                this.form.selectedNotificationTypesSearchQuery = query;
            } else {
                this.form.selectedNotificationTypesSearchQuery = "";
            }
        },
        onRemoveSelectedNotificationType(index) {
            this.$delete(this.form.selectedNotificationTypes, index);
        },
    },
};
</script>
